/**
 * @file CategoryImageField.tsx
 * @description Фото категории (phase19.16 Р2а; uiux-design-r2a §2;
 * db-design-r2a, ответ 8; контракт §19.8; решение пользователя 2026-10-12):
 * фото из медиатеки магазина (`imageMediaId`) квадратом 160 с точкой фокуса
 * — на сайте это квадратная плитка в блоках категорий и подкатегорий, а
 * баннер страницы категории режется из того же фото по фокусу (включается
 * в «Сайт → Каталог»). Выбор, фокус и «Убрать» сохраняются сразу (PATCH
 * категории). Фото, загруженное по-старому (без медиатеки), видно, но
 * фокус у него задать нельзя — его заменяют фото из медиатеки.
 */

import { useState } from "react";

import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { useNotify } from "../ui/NotifyProvider";
import { FocalPointPicker } from "../site-builder/section/fields/FocalPointPicker";
import { MediaPickerDialog } from "../site-view/media/MediaPickerDialog";
import { catalogCategories, type CatalogCategory, type UpdateCategoryInput } from "../../lib/api-catalog";
import { ApiError } from "../../lib/api-client";

/** Тексты поля (uiux-design-r2a §2). */
export const CATEGORY_IMAGE_TEXT = {
  hint: "Показывается квадратной плиткой в блоках категорий на сайте; рекомендуемый размер от 800×800, ключевой объект — в центре. Баннер на странице категории включается в «Сайт → Каталог».",
  empty: "Фото нет — на сайте плитка категории будет цвета плашки с названием.",
  legacy: "Фото загружено по-старому — выберите фото из медиатеки, чтобы задать точку фокуса.",
} as const;

const ERROR_TEXT: Readonly<Record<string, string>> = {
  category_image_media_invalid: "Это фото нельзя поставить: оно не из медиатеки магазина или это не картинка.",
  category_image_focus_invalid: "Точка фокуса не сохранилась — нажмите на фото ещё раз.",
};

interface Props {
  category: CatalogCategory;
  onChanged: (category: CatalogCategory) => void;
}

const CENTER = { x: 50, y: 50 };

export function CategoryImageField({ category, onChanged }: Props) {
  const notify = useNotify();
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const mediaId = category.imageMediaId ?? null;
  const legacy = !mediaId && !!category.imageUrl;

  async function save(input: UpdateCategoryInput, done?: string) {
    setBusy(true);
    try {
      onChanged(await catalogCategories.update(category.id, input));
      if (done) notify.success(done);
    } catch (err) {
      notify.error(err instanceof ApiError ? (ERROR_TEXT[err.code] ?? err.message) : "Не удалось сохранить фото категории.");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    // Прежнее фото (без медиатеки) убирается старым путём — PATCH его не знает.
    if (legacy) {
      setBusy(true);
      try {
        onChanged(await catalogCategories.removeImage(category.id));
        notify.success("Фото категории убрано");
      } catch {
        notify.error("Не удалось убрать фото категории.");
      } finally {
        setBusy(false);
      }
      return;
    }
    await save({ imageMediaId: null, imageFocusX: null, imageFocusY: null }, "Фото категории убрано");
  }

  return (
    <div className="flex flex-col gap-2">
      <Label>Фото категории</Label>
      {mediaId ? (
        <FocalPointPicker
          mediaId={mediaId}
          value={category.imageFocus ?? CENTER}
          alt={category.displayName}
          className="size-40"
          onChange={(focus) => void save({ imageFocusX: focus.x, imageFocusY: focus.y })}
        />
      ) : legacy ? (
        <>
          <img src={category.imageUrl ?? ""} alt={category.displayName} className="size-40 rounded-xl object-cover" />
          <p className="type-caption text-on-surface-variant">{CATEGORY_IMAGE_TEXT.legacy}</p>
        </>
      ) : (
        <p className="type-caption text-on-surface-variant">{CATEGORY_IMAGE_TEXT.empty}</p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" disabled={busy} onClick={() => setPicking(true)}>
          {mediaId || legacy ? "Заменить" : "Выбрать из медиатеки"}
        </Button>
        {mediaId || legacy ? (
          <Button type="button" variant="ghost" disabled={busy} onClick={() => void remove()}>
            Убрать фото
          </Button>
        ) : null}
      </div>
      <p className="type-caption text-on-surface-variant">{CATEGORY_IMAGE_TEXT.hint}</p>
      <MediaPickerDialog
        open={picking}
        title="Фото категории"
        hint="JPEG, PNG или WebP от 800×800. Ключевой объект — в центре."
        accept={["image/jpeg", "image/png", "image/webp"]}
        cropAspect={1}
        onClose={() => setPicking(false)}
        onSelect={(media) => {
          setPicking(false);
          void save({ imageMediaId: media.id, imageFocusX: CENTER.x, imageFocusY: CENTER.y }, "Фото категории сохранено");
        }}
      />
    </div>
  );
}
